import { useEffect, useMemo, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import CrudService from "services/cruds-service";
import Core from "./Core";
import Card from "@mui/material/Card";
import { Dialog, FormGroup, FormControlLabel, Checkbox } from "@mui/material";

function DataTable({
  entriesPerPage,
  canSearch,
  canAddCol,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  setPage,
  meta,
  pageNum,
  setSortVal,
  setLimitChange,
  filterTxt,
}) {
  const data = useMemo(() => table.rows, [table]);

  const [open, setOpen] = useState(false);
  const [col, setCol] = useState(table.columns);

  useEffect(() => {
    setCol(table.columns);
  }, [table.columns]);

  // Handler function to toggle column visibility
  const handleChange = (event) => {
    const { name } = event.target;

    setCol((prevCol) =>
      prevCol.map((column) =>
        column.accessor === name
          ? { ...column, hidden: !column.hidden }
          : column
      )
    );
  };

  const visibleColumns = useMemo(() => col.filter((c) => !c.hidden), [col]);

  return (
    <Card>
      <Dialog onClose={() => setOpen(false)} open={open}>
        <FormGroup sx={{ margin: "20px", padding: "10px" }}>
          {col.map((c) => (
            <FormControlLabel
              key={c.accessor}
              control={
                <Checkbox
                  checked={!c.hidden}
                  onChange={handleChange}
                  name={c.accessor}
                />
              }
              label={c.Header}
            />
          ))}
        </FormGroup>
      </Dialog>
      <Core
        setLimitChange={setLimitChange}
        table={{ columns: visibleColumns, rows: data }}
        meta={meta}
        setPage={setPage}
        setSortVal={setSortVal}
        pageNum={pageNum}
        isSorted={isSorted}
        canAddCol={canAddCol}
        setOpen={setOpen}
        canSearch={canSearch}
        entriesPerPage={entriesPerPage}
        showTotalEntries={showTotalEntries}
        pagination={pagination}
        noEndBorder={noEndBorder}
        filterTxt={filterTxt}
      />
    </Card>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: {
    defaultValue: CrudService.getLimit(),
    entries: [5, 10, 15, 20, 25, 50, 100],
  },
  canSearch: false,
  canAddCol: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  canAddCol: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
